import * as Sentry from '@sentry/vue'
import { isEmpty } from 'lodash'
import { scrubBreadcrumb, scrubEvent, scrubInternalEvent } from '~/utils/sentry-event-privacy'
import {
  isReplayExcludedPath,
  REPLAY_BLOCKED_SELECTORS,
  REPLAY_MASKED_ATTRIBUTES,
  scrubReplayEvent,
  scrubReplayRecordingEvent,
} from '~/utils/sentry-replay-privacy'

async function lazyLoadSentryIntegrations() {
  // don't load on server
  if (!process.client) return

  // Replay cannot filter the page address it records. Don't start it on pages whose
  // address holds a secret (see utils/sentry-replay-privacy.js).
  if (isReplayExcludedPath(window.location.pathname)) return

  const { Replay } = await import('@sentry/vue')

  // Clean URLs and user details on replay events. Other events are not changed.
  Sentry.addEventProcessor(scrubReplayEvent)

  Sentry.addIntegration(
    new Replay({
      // Keep Sentry's privacy defaults: mask all text and inputs, block all media.
      // Don't capture network request or response details (no networkDetailAllowUrls).
      maskAllText: true,
      maskAllInputs: true,
      blockAllMedia: true,
      // See utils/sentry-replay-privacy.js for what these cover.
      maskAttributes: REPLAY_MASKED_ATTRIBUTES,
      block: REPLAY_BLOCKED_SELECTORS,
      beforeAddRecordingEvent: scrubReplayRecordingEvent,
    }),
  )
}

function getSentryIntegrations() {
  // don't load on server
  if (!process.client) return []

  const router = useRouter()

  // Console errors with an Error object (for example failed API calls) stay as events.
  // scrubEvent() drops console events that have only text. See utils/sentry-event-privacy.js.
  return [Sentry.browserTracingIntegration({ router }), Sentry.captureConsoleIntegration({ levels: ['error'] })]
}

export default defineNuxtPlugin({
  name: 'sentry',
  parallel: true,
  async setup(nuxtApp) {
    const vueApp = nuxtApp.vueApp

    const {
      public: { sentry, environment },
    } = useRuntimeConfig()

    if (isEmpty(sentry.dsn)) {
      return
    }

    const messagesToIntercept = [
      '****************************************************************************************************************************',
      '************************************************ AG Grid Enterprise License ************************************************',
      '************************************************** License Key Not Found ***************************************************',
      '* All AG Grid Enterprise features are unlocked for trial.                                                                  *',
      '* If you want to hide the watermark please email info@ag-grid.com for a trial license key.                                 *',
      'Please see https://www.ag-grid.com/javascript-grid/modules/ for more information.',
      'AG Grid:',
    ]

    Sentry.init({
      app: vueApp,
      dsn: sentry.dsn,
      environment,
      integrations: getSentryIntegrations(),

      // Don't send personal data: no IP address, no Vue component props.
      // utils/sentry-event-privacy.js cleans the rest of each event.
      sendDefaultPii: false,
      attachProps: false,

      // Set tracesSampleRate to 1.0 to capture 100%
      // of transactions for performance monitoring.
      // We recommend adjusting this value in production
      tracesSampleRate: sentry.trace_rate,

      // Set `tracePropagationTargets` to control for which URLs distributed tracing should be enabled
      // tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],

      // This sets the sample rate. You may want this to be 100% while
      // in development and sample at a lower rate in production
      replaysSessionSampleRate: sentry.replay_rate,

      // If the entire session is not sampled, use the below sample rate to sample
      // sessions when an error occurs.
      replaysOnErrorSampleRate: sentry.replay_error_rate,

      beforeSend(event, hint) {
        const error = hint.originalException

        if (error && error?.status === 401) {
          return null
        }

        if (error?.request && error?.request?.responseURL && error?.request?.responseURL.includes('/login')) {
          return null
        }

        if (event.message && messagesToIntercept.some((msg) => event.message?.includes(msg))) {
          return null
        }

        return scrubEvent(event, hint)
      },

      beforeSendTransaction(event, hint) {
        return scrubEvent(event, hint)
      },

      beforeBreadcrumb(breadcrumb) {
        return scrubBreadcrumb(breadcrumb)
      },
    })

    // Sentry skips beforeSend for its own events about errors in the event pipeline.
    Sentry.addEventProcessor(scrubInternalEvent)

    // Lazy-load the replay integration to reduce bundle size
    lazyLoadSentryIntegrations()

    return {
      provide: { Sentry },
    }
  },
})
